/**
 * What a @-mention looks like inside a comment body.
 *
 * Mentions are stored as markdown links: `[@Ana Silva](mention:<employeeId>)`.
 * This is an ordinary commonmark link, so it round-trips through remark with no
 * schema work. The id is authoritative; the label is a snapshot — a renamed
 * person keeps the old label in old comments.
 *
 * The scheme is `mention:<uuid>`, mirroring `attachment:<id>`. The regex
 * matches the same shape the server's extractor uses, kept in step by shape
 * (they are in different packages; a comment in each pointing at the other is
 * enough).
 */

const SCHEME = "mention:"

/** The regex for a mention link in markdown. Shared with the server's extractor by shape. */
export const MENTION_LINK = /\[[^\]]*\]\(mention:([0-9a-fA-F-]{36})\)/g

/** The `mention:<id>` form for an employee id. */
export function mentionRef(id: string): string {
  return `${SCHEME}${id}`
}

/** The id inside a `mention:<id>`, or null for anything else. A link that is
 *  not a mention yields null, which the renderer treats as a normal link. */
export function refId(href: string): string | null {
  return href.startsWith(SCHEME) ? href.slice(SCHEME.length) : null
}

/** Whether an href is a mention ref. */
export function isMentionRef(href: string): boolean {
  return href.startsWith(SCHEME)
}

/** The text a mention pill shows. The `@` is stored in the label, so this is
 *  usually the label untouched — it only adds one for a mention written by
 *  hand without it, so the pill never reads as a bare name. */
export function mentionLabel(label: string): string {
  const trimmed = label.trim()
  if (!trimmed) return "@mention"
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`
}
